import { Stack, Text } from "@certn/cascadia";
import { Card, Illustration } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { EnrollmentDetailCheck } from "../types";
import {
  CheckMonitoredCard,
  type CheckMonitoredCardProps,
} from "./CheckMonitoredCard";
import { monitoringTabCardContainerStyle } from "./monitoringStyles";

export interface ChecksMonitoredTabProps {
  enrollments: EnrollmentDetailCheck[];
  headerActions?: ReactNode;
  description?: string;
  emptyMessage?: string;
  renderEnrollmentCard?: (enrollment: EnrollmentDetailCheck) => ReactNode;
  renderCardActions?: CheckMonitoredCardProps["renderActions"];
  renderCaseLink?: CheckMonitoredCardProps["renderCaseLink"];
  renderDemoResponseEditor?: CheckMonitoredCardProps["renderDemoResponseEditor"];
}

export function ChecksMonitoredTab({
  enrollments,
  headerActions,
  description,
  emptyMessage,
  renderEnrollmentCard,
  renderCardActions,
  renderCaseLink,
  renderDemoResponseEditor,
}: ChecksMonitoredTabProps) {
  const { t } = useTranslation();
  const resolvedEmptyMessage =
    emptyMessage ?? t("This applicant has no checks being monitored.");

  return (
    <Card
      className={monitoringTabCardContainerStyle}
      type="large"
      responsive
      testID="monitoring-checks-card"
    >
      <div className={styles.checksMonitored}>
        <div className={styles.header}>
          <Stack gap="space-2">
            <Text testID="monitoring-checks-title" style="global/heading-large">
              {t("Checks monitored")}
            </Text>
            {description ? (
              <Text style="form/body-secondary" color="text-weak">
                {description}
              </Text>
            ) : null}
          </Stack>
          {headerActions ? (
            <div className={styles.headerActions}>{headerActions}</div>
          ) : null}
        </div>
        {enrollments.length === 0 ? (
          <Stack
            align="center"
            gap="space-4"
            className={styles.emptyState}
            testID="monitoring-checks-empty"
          >
            <Illustration aria-hidden="true" name="sparkles" />
            <Stack align="center" gap="space-2">
              <Text
                style="form/body-secondary"
                color="text-weak"
                align="center"
              >
                {resolvedEmptyMessage}
              </Text>
            </Stack>
          </Stack>
        ) : (
          <div
            className={styles.checkCards}
            data-testid="monitoring-checks-list"
          >
            {enrollments.map((enrollment) =>
              renderEnrollmentCard ? (
                <div key={enrollment.id}>
                  {renderEnrollmentCard(enrollment)}
                </div>
              ) : (
                <CheckMonitoredCard
                  key={enrollment.id}
                  enrollment={enrollment}
                  renderActions={renderCardActions}
                  renderCaseLink={renderCaseLink}
                  renderDemoResponseEditor={renderDemoResponseEditor}
                />
              )
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

const styles = {
  checksMonitored: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-6)",
  }),
  header: css({
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "var(--space-3)",
  }),
  headerActions: css({
    flexShrink: 0,
  }),
  checkCards: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-8)",
    width: "100%",
  }),
  emptyState: css({
    paddingTop: "var(--space-4)",
  }),
};
